import { fetchAuthSession } from 'aws-amplify/auth';

export interface SubdomainConfig {
  subdomain: string;
  user_id: string;
  created_at: string;
  enabled: boolean;
  rate_limit_per_minute: number;
}

export interface UserFile {
  name: string;
  size_bytes: number;
  last_modified?: string;
  is_chunked: boolean;
}

export interface QueryResult {
  success: boolean;
  data: Record<string, any>[];
  row_count: number;
  execution_time_ms: number;
  truncated: boolean;
  error?: string;
}

export interface FileSchema {
  file?: string;
  columns?: { name: string; type: string; nullable?: boolean }[];
  schema?: Record<string, string>;
  error?: string;
}

// Developer API domain suffix
const API_DOMAIN = 'bigsplicer.com';

// DynamoDB tables accessed via the main API Gateway
const CONFIG_API_ENDPOINT = 'https://jtbb58634f.execute-api.us-east-1.amazonaws.com/prod';

class SubdomainApiService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  /**
   * Get API endpoint for a given subdomain
   */
  getApiEndpoint(subdomain?: string): string {
    if (!subdomain) {
      throw new Error('No API subdomain configured. Visit the Developer API page to set one up.');
    }
    return `https://${subdomain}.${API_DOMAIN}`;
  }

  /**
   * Get user's subdomain configuration
   */
  async getSubdomainConfig(): Promise<SubdomainConfig | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${CONFIG_API_ENDPOINT}/subdomain-config`, {
        method: 'GET',
        headers,
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch subdomain config');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching subdomain config:', error);
      return null;
    }
  }

  /**
   * Check if a subdomain is available
   */
  async checkSubdomainAvailability(subdomain: string): Promise<{ available: boolean; message?: string }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${CONFIG_API_ENDPOINT}/subdomain-check?subdomain=${encodeURIComponent(subdomain)}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const data = await response.json();
        return { available: false, message: data.error || 'Error checking availability' };
      }

      const data = await response.json();
      return { available: data.available, message: data.message };
    } catch (error) {
      return { available: false, message: 'Error checking availability' };
    }
  }

  /**
   * Save or update subdomain configuration
   */
  async saveSubdomainConfig(subdomain: string, enabled: boolean = true): Promise<SubdomainConfig> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${CONFIG_API_ENDPOINT}/subdomain-config`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ subdomain, enabled }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to save subdomain config');
    }

    return await response.json();
  }

  /**
   * List user's files via subdomain API
   */
  async listFiles(apiKey: string, subdomain?: string): Promise<UserFile[]> {
    const endpoint = this.getApiEndpoint(subdomain);
    const response = await fetch(`${endpoint}/files`, {
      method: 'GET',
      headers: {
        'X-API-Key': apiKey,
      },
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to list files');
    }

    const data = await response.json();
    return data.files || [];
  }

  /**
   * Get schema for a file via subdomain API
   */
  async getFileSchema(apiKey: string, filename: string, subdomain?: string): Promise<FileSchema> {
    const endpoint = this.getApiEndpoint(subdomain);
    const response = await fetch(`${endpoint}/schema/${encodeURIComponent(filename)}`, {
      method: 'GET',
      headers: {
        'X-API-Key': apiKey,
      },
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to get schema');
    }

    const data = await response.json();
    // Normalize response from devapi (which returns {data: {col: type}})
    if (data.data && typeof data.data === 'object') {
      return { schema: data.data, file: filename };
    }
    return data;
  }

  /**
   * Execute a Polars query via subdomain API
   */
  async executeQuery(
    apiKey: string,
    filename: string,
    code: string,
    limit: number = 1000,
    subdomain?: string
  ): Promise<QueryResult> {
    const endpoint = this.getApiEndpoint(subdomain);
    const startTime = Date.now();
    const response = await fetch(`${endpoint}/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      },
      body: JSON.stringify({
        file: filename,
        code,
        limit,
      }),
    });

    const elapsedMs = Date.now() - startTime;
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        data: [],
        row_count: 0,
        execution_time_ms: elapsedMs,
        truncated: false,
        error: data.error || 'Query execution failed',
      };
    }

    // Normalize response from devapi (which returns {success, data})
    const resultData = data.data || [];
    return {
      success: data.success ?? true,
      data: resultData,
      row_count: resultData.length,
      execution_time_ms: data.execution_time_ms || elapsedMs,
      truncated: data.truncated || resultData.length >= limit,
      error: data.error,
    };
  }

  /**
   * Generate curl command for a query
   */
  generateCurlCommand(
    apiKey: string,
    filename: string,
    code: string,
    limit: number = 1000,
    subdomain?: string
  ): string {
    const endpoint = this.getApiEndpoint(subdomain);
    const body = JSON.stringify({ file: filename, code, limit });
    return `curl -X POST '${endpoint}/query' \\
  -H 'Content-Type: application/json' \\
  -H 'X-API-Key: ${apiKey}' \\
  -d '${body.replace(/'/g, "\\'")}'`;
  }

  /**
   * Generate Python code for a query
   */
  generatePythonCode(
    apiKey: string,
    filename: string,
    code: string,
    limit: number = 1000,
    subdomain?: string
  ): string {
    const endpoint = this.getApiEndpoint(subdomain);
    return `import requests

API_KEY = "${apiKey}"
ENDPOINT = "${endpoint}"

response = requests.post(
    f"{ENDPOINT}/query",
    headers={
        "Content-Type": "application/json",
        "X-API-Key": API_KEY,
    },
    json={
        "file": "${filename}",
        "code": """${code}""",
        "limit": ${limit}
    }
)

result = response.json()
if result.get("success"):
    print(f"Rows: {result['row_count']}, Time: {result['execution_time_ms']}ms")
    for row in result["data"][:5]:
        print(row)
else:
    print(f"Error: {result.get('error')}")`;
  }

  /**
   * Generate JavaScript/Node.js code for a query
   */
  generateJavaScriptCode(
    apiKey: string,
    filename: string,
    code: string,
    limit: number = 1000,
    subdomain?: string
  ): string {
    const endpoint = this.getApiEndpoint(subdomain);
    return `const API_KEY = "${apiKey}";
const ENDPOINT = "${endpoint}";

const response = await fetch(\`\${ENDPOINT}/query\`, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": API_KEY,
  },
  body: JSON.stringify({
    file: "${filename}",
    code: \`${code}\`,
    limit: ${limit}
  })
});

const result = await response.json();
if (result.success) {
  console.log(\`Rows: \${result.row_count}, Time: \${result.execution_time_ms}ms\`);
  console.log(result.data.slice(0, 5));
} else {
  console.error(\`Error: \${result.error}\`);
}`;
  }
}

export const subdomainApiService = new SubdomainApiService();
